import moment from 'moment';
import { pipe } from 'ramda';
import map from 'lodash/fp/map';
import keyBy from 'lodash/fp/keyBy';
import { getTimezone } from '../../auth/selectors';
import { store } from '../../../main';
import { t } from '../../../modules/infra/i18n';

class Preset {
  static create(options) {
    return new Preset(options);
  }

  constructor({ from, to, label }) {
    this.id = label;
    this.from = from;
    this.to = to;
    this.label = label;
  }

  getRange() {
    return {
      from: this.from(),
      to: this.to(),
    };
  }

  equals({ from, to }) {
    return moment(this.from()).isSame(from, 'd') &&
      moment(this.to()).isSame(to, 'd');
  }
}

class Presets {
  static create(options) {
    return new Presets(options);
  }

  constructor(presets) {
    this.presets = pipe(
      map(Preset.create),
      keyBy('id'),
    )(presets);
  }

  getRange(key) {
    const preset = this.presets[key];
    if (preset) return preset.getRange();
    return {};
  }

  map(fn) {
    return Object.values(this.presets).map(fn);
  }

  find(from, to) {
    const preset = Object.values(this.presets).find(item => item.equals({ from, to }));
    if (preset) return preset;
    return null;
  }

  findAll(from, to) {
    return Object.values(this.presets).filter(item => item.equals({ from, to }));
  }
}

export const noPresets = Presets.create([]);

export const getCustomerRetentionPresets = () => Presets.create([
  {
    label: t('common:datepicker.presets.last_month'),
    from: () => moment()
      .startOf('month')
      .tz(getTimezone(store.getState()))
      .subtract(1, 'month')
      .toDate(),
    to: () => moment()
      .endOf('month')
      .tz(getTimezone(store.getState()))
      .subtract(1, 'month')
      .toDate(),
  },
  {
    label: t('common:datepicker.presets.last_2_months'),
    from: () => moment()
      .startOf('month')
      .tz(getTimezone(store.getState()))
      .subtract(2, 'month')
      .toDate(),
    to: () => moment()
      .endOf('month')
      .tz(getTimezone(store.getState()))
      .subtract(1, 'month')
      .toDate(),
  },
  {
    label: t('common:datepicker.presets.last_3_months'),
    from: () => moment()
      .startOf('month')
      .tz(getTimezone(store.getState()))
      .subtract(3, 'month')
      .toDate(),
    to: () => moment()
      .endOf('month')
      .tz(getTimezone(store.getState()))
      .subtract(1, 'month')
      .toDate(),
  },
  {
    label: t('common:datepicker.presets.last_quarter'),
    from: () => moment()
      .startOf('quarter')
      .tz(getTimezone(store.getState()))
      .subtract(1, 'quarter')
      .toDate(),
    to: () => moment()
      .endOf('quarter')
      .tz(getTimezone(store.getState()))
      .subtract(1, 'quarter')
      .toDate(),
  },
]);

// Static version for backward compatibility
export const customerRetention = Presets.create([
  {
    label: 'Last Month',
    from: () => moment()
      .startOf('month')
      .tz(getTimezone(store.getState()))
      .subtract(1, 'month')
      .toDate(),
    to: () => moment()
      .endOf('month')
      .tz(getTimezone(store.getState()))
      .subtract(1, 'month')
      .toDate(),
  },
  {
    label: 'Last 2 Months',
    from: () => moment()
      .startOf('month')
      .tz(getTimezone(store.getState()))
      .subtract(2, 'month')
      .toDate(),
    to: () => moment()
      .endOf('month')
      .tz(getTimezone(store.getState()))
      .subtract(1, 'month')
      .toDate(),
  },
  {
    label: 'Last 3 Months',
    from: () => moment()
      .startOf('month')
      .tz(getTimezone(store.getState()))
      .subtract(3, 'month')
      .toDate(),
    to: () => moment()
      .endOf('month')
      .tz(getTimezone(store.getState()))
      .subtract(1, 'month')
      .toDate(),
  },
  {
    label: 'Last Quarter',
    from: () => moment()
      .startOf('quarter')
      .tz(getTimezone(store.getState()))
      .subtract(1, 'quarter')
      .toDate(),
    to: () => moment()
      .endOf('quarter')
      .tz(getTimezone(store.getState()))
      .subtract(1, 'quarter')
      .toDate(),
  },
]);

export const getDefaultPresets = () => Presets.create([
  {
    label: t('common:datepicker.presets.today'),
    from: () => moment()
      .tz(getTimezone(store.getState()))
      .toDate(),
    to: () => moment()
      .tz(getTimezone(store.getState()))
      .toDate(),
  },
  {
    label: t('common:datepicker.presets.yesterday'),
    from: () => moment()
      .startOf('day')
      .tz(getTimezone(store.getState()))
      .subtract(1, 'days')
      .toDate(),
    to: () => moment()
      .endOf('day')
      .tz(getTimezone(store.getState()))
      .subtract(1, 'days')
      .toDate(),
  },
  {
    label: t('common:datepicker.presets.this_week'),
    from: () => moment()
      .startOf('week')
      .tz(getTimezone(store.getState()))
      .toDate(),
    to: () => moment()
      .tz(getTimezone(store.getState()))
      .toDate(),
  },
  {
    label: t('common:datepicker.presets.this_month'),
    from: () => moment()
      .startOf('month')
      .tz(getTimezone(store.getState()))
      .toDate(),
    to: () => moment()
      .tz(getTimezone(store.getState()))
      .toDate(),
  },
  {
    label: t('common:datepicker.presets.this_quarter'),
    from: () => moment()
      .startOf('quarter')
      .tz(getTimezone(store.getState()))
      .toDate(),
    to: () => moment()
      .tz(getTimezone(store.getState()))
      .toDate(),
  },
  {
    label: t('common:datepicker.presets.this_year'),
    from: () => moment()
      .startOf('year')
      .tz(getTimezone(store.getState()))
      .toDate(),
    to: () => moment()
      .tz(getTimezone(store.getState()))
      .toDate(),
  },
  {
    label: t('common:datepicker.presets.last_year'),
    from: () => moment()
      .startOf('year')
      .tz(getTimezone(store.getState()))
      .subtract(1, 'year')
      .toDate(),
    to: () => moment()
      .endOf('year')
      .tz(getTimezone(store.getState()))
      .subtract(1, 'year')
      .toDate(),
  },
]);

// Static version for backward compatibility
export const defaultPresets = Presets.create([
  {
    label: 'Today',
    from: () => moment()
      .tz(getTimezone(store.getState()))
      .toDate(),
    to: () => moment()
      .tz(getTimezone(store.getState()))
      .toDate(),
  },
  {
    label: 'Yesterday',
    from: () => moment()
      .startOf('day')
      .tz(getTimezone(store.getState()))
      .subtract(1, 'days')
      .toDate(),
    to: () => moment()
      .endOf('day')
      .tz(getTimezone(store.getState()))
      .subtract(1, 'days')
      .toDate(),
  },
  {
    label: 'This Week',
    from: () => moment()
      .startOf('week')
      .tz(getTimezone(store.getState()))
      .toDate(),
    to: () => moment()
      .tz(getTimezone(store.getState()))
      .toDate(),
  },
  {
    label: 'This Month',
    from: () => moment()
      .startOf('month')
      .tz(getTimezone(store.getState()))
      .toDate(),
    to: () => moment()
      .tz(getTimezone(store.getState()))
      .toDate(),
  },
  {
    label: 'This Quarter',
    from: () => moment()
      .startOf('quarter')
      .tz(getTimezone(store.getState()))
      .toDate(),
    to: () => moment()
      .tz(getTimezone(store.getState()))
      .toDate(),
  },
  {
    label: 'This Year',
    from: () => moment()
      .startOf('year')
      .tz(getTimezone(store.getState()))
      .toDate(),
    to: () => moment()
      .tz(getTimezone(store.getState()))
      .toDate(),
  },
  {
    label: 'Last Year',
    from: () => moment()
      .startOf('year')
      .tz(getTimezone(store.getState()))
      .subtract(1, 'year')
      .toDate(),
    to: () => moment()
      .endOf('year')
      .tz(getTimezone(store.getState()))
      .subtract(1, 'year')
      .toDate(),
  },
]);

export const getUserLocalDatePresets = () => Presets.create([
  {
    label: t('common:datepicker.presets.today'),
    from: () => moment()
      .startOf('day')
      .toDate(),
    to: () => moment()
      .endOf('day')
      .toDate(),
  },
  {
    label: t('common:datepicker.presets.yesterday'),
    from: () => moment()
      .startOf('day')
      .subtract(1, 'days')
      .toDate(),
    to: () => moment()
      .endOf('day')
      .subtract(1, 'days')
      .toDate(),
  },
  {
    label: t('common:datepicker.presets.this_week'),
    from: () => moment()
      .startOf('week')
      .toDate(),
    to: () => moment()
      .endOf('day')
      .toDate(),
  },
  {
    label: t('common:datepicker.presets.this_month'),
    from: () => moment()
      .startOf('month')
      .toDate(),
    to: () => moment()
      .endOf('day')
      .toDate(),
  },
  {
    label: t('common:datepicker.presets.this_quarter'),
    from: () => moment()
      .startOf('quarter')
      .toDate(),
    to: () => moment()
      .endOf('day')
      .toDate(),
  },
  {
    label: t('common:datepicker.presets.this_year'),
    from: () => moment()
      .startOf('year')
      .toDate(),
    to: () => moment()
      .endOf('day')
      .toDate(),
  },
  {
    label: t('common:datepicker.presets.last_year'),
    from: () => moment()
      .startOf('year')
      .subtract(1, 'year')
      .toDate(),
    to: () => moment()
      .endOf('year')
      .subtract(1, 'year')
      .toDate(),
  },
]);

// Static version for backward compatibility
export const userLocalDatePresets = Presets.create([
  {
    label: 'Today',
    from: () => moment()
      .startOf('day')
      .toDate(),
    to: () => moment()
      .endOf('day')
      .toDate(),
  },
  {
    label: 'Yesterday',
    from: () => moment()
      .startOf('day')
      .subtract(1, 'days')
      .toDate(),
    to: () => moment()
      .endOf('day')
      .subtract(1, 'days')
      .toDate(),
  },
  {
    label: 'This Week',
    from: () => moment()
      .startOf('week')
      .toDate(),
    to: () => moment()
      .endOf('day')
      .toDate(),
  },
  {
    label: 'This Month',
    from: () => moment()
      .startOf('month')
      .toDate(),
    to: () => moment()
      .endOf('day')
      .toDate(),
  },
  {
    label: 'This Quarter',
    from: () => moment()
      .startOf('quarter')
      .toDate(),
    to: () => moment()
      .endOf('day')
      .toDate(),
  },
  {
    label: 'This Year',
    from: () => moment()
      .startOf('year')
      .toDate(),
    to: () => moment()
      .endOf('day')
      .toDate(),
  },
  {
    label: 'Last Year',
    from: () => moment()
      .startOf('year')
      .subtract(1, 'year')
      .toDate(),
    to: () => moment()
      .endOf('year')
      .subtract(1, 'year')
      .toDate(),
  },
]);
